/**
 * Mobile App Prototype
 *
 * Simulates the core FlowNotes mobile UX inside an iPhone frame:
 * - Inbox feed with thread cards and filter pills
 * - Thread detail with quick reply and "Open in Chat" escalation
 * - Hub-based session switching via bottom sheet
 * - Browse: folder tree (content-root style paths) + searchable threads/docs (mock)
 * - Agent chat surface primed with hub context
 * - Quick capture sheet
 *
 * Access: http://flownotes.localhost/?test=mobile-app
 *
 * Next steps (prototype): wire Browse to real folder/document APIs + search;
 * document preview sheet; hub-context parity with ADR-0005 on native app.
 */

import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from 'react'
import {
  Inbox, MessageSquare, Search, User, Plus, ChevronLeft,
  ChevronRight, Zap, Mic, Link2, FileText, Archive,
  Send, X, CheckCircle2, MoreHorizontal, ArrowUpRight,
  Sparkles, AlignJustify, AlertCircle, Folder,
} from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

type Screen = 'inbox' | 'chat' | 'browse' | 'profile'
type BrowseSegment = 'folders' | 'search'
type ThreadKind = 'flow' | 'captured' | 'prompt' | 'digest' | 'reply'
type InboxFilter = 'all' | 'flows' | 'captured' | 'prompts'

interface Hub {
  id: string
  name: string
  shortName: string
  color: string
  pendingCount: number
  lastActivity: string
}

interface Thread {
  id: string
  kind: ThreadKind
  title: string
  preview: string
  body?: string
  hubId: string
  time: string
  unread: boolean
}

interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  time: string
}

// ============================================================================
// Mock data
// ============================================================================

const HUBS: Hub[] = [
  { id: 'hop', name: 'Hop Studios', shortName: 'Hop', color: '#5B6AD0', pendingCount: 4, lastActivity: '2 min ago' },
  { id: 'ggsc', name: 'GGSC', shortName: 'GG', color: '#2D9B6E', pendingCount: 7, lastActivity: '12 min ago' },
  { id: 'personal', name: 'Personal', shortName: 'Me', color: '#B5722A', pendingCount: 1, lastActivity: '1h ago' },
]

const THREADS: Thread[] = [
  {
    id: 't1', kind: 'prompt',
    title: 'GGSC homepage redesign - ready to review',
    preview: 'The flow completed its analysis. 3 copy changes need your approval before the PR can merge.',
    body: 'The flow has completed its analysis pass on the proposed homepage copy. Three changes are flagged for your approval before the PR can merge. Each change is minor but requires your sign-off given the copy is client-facing.',
    hubId: 'ggsc', time: '2m', unread: true,
  },
  {
    id: 't2', kind: 'flow',
    title: 'Daily digest - Hop Studios',
    preview: '4 Basecamp todos moved, 2 new comments from Travis, 1 MR opened on the FlowNotes repo.',
    hubId: 'hop', time: '8m', unread: true,
  },
  {
    id: 't3', kind: 'captured',
    title: 'Link saved from Safari',
    preview: 'nngroup.com/articles/mobile-navigation-patterns - captured while reading on iPhone',
    hubId: 'personal', time: '34m', unread: false,
  },
  {
    id: 't4', kind: 'flow',
    title: 'Basecamp weekly report - GGSC',
    preview: '12 todos reviewed, 3 overdue flagged (Survey followup, Email campaign, Blog draft), pushes recommended.',
    hubId: 'ggsc', time: '1h', unread: false,
  },
  {
    id: 't5', kind: 'reply',
    title: 'Conversation: project naming',
    preview: 'You: "what are the strongest candidates that lean into the session-portability angle?"',
    hubId: 'hop', time: '3h', unread: false,
  },
  {
    id: 't6', kind: 'digest',
    title: 'Morning brief - May 9',
    preview: 'Today: 3 meetings, 2 deadlines, GGSC survey closes tonight. Hop internal sync at 2pm.',
    hubId: 'personal', time: '8h', unread: false,
  },
]

const CHAT_MESSAGES: ChatMessage[] = [
  { id: 'm1', role: 'user', content: "What's the status of the GGSC homepage redesign?", time: '11:42 AM' },
  { id: 'm2', role: 'assistant', content: "At final review stage. The flow analysis is complete - 3 copy changes are waiting for your approval in the inbox. Once you approve, the PR can merge. Travis marked it high priority for this sprint.", time: '11:42 AM' },
  { id: 'm3', role: 'user', content: 'How many open Basecamp todos are left in GGSC this week?', time: '11:44 AM' },
  { id: 'm4', role: 'assistant', content: "7 open todos total. 3 are overdue: the survey followup (due May 6), the email campaign draft (May 7), and the blog post (May 8). I can push all three to next Monday - should I queue that up?", time: '11:44 AM' },
  { id: 'm5', role: 'user', content: 'Yes, push them all to Monday.', time: '11:45 AM' },
  { id: 'm6', role: 'assistant', content: "Done. 3 todos pushed to Monday May 13. Basecamp updated. I've noted the pushes in the weekly report thread so Travis sees the changes at next sync.", time: '11:45 AM' },
  { id: 'm7', role: 'user', content: 'Any flows waiting on me right now?', time: '11:46 AM' },
  { id: 'm8', role: 'assistant', content: "Just the one: the homepage redesign copy review in your inbox. That's it. Everything else is either running autonomously or waiting on Travis.", time: '11:46 AM' },
]

/** One tree per workspace hub id (mock paths mirror flownotes-content layout). */
interface FolderEntry {
  id: string
  name: string
  kind: 'folder' | 'doc'
  pathLabel: string
  children?: FolderEntry[]
}

const MOCK_FOLDER_ROOT_BY_HUB: Record<string, FolderEntry> = {
  hop: {
    id: 'hop-root',
    name: 'hop-studios',
    kind: 'folder',
    pathLabel: 'hop-studios',
    children: [
      {
        id: 'hop-clients',
        name: 'clients',
        kind: 'folder',
        pathLabel: 'hop-studios/clients',
        children: [
          {
            id: 'hop-gg',
            name: 'greater-good',
            kind: 'folder',
            pathLabel: 'hop-studios/clients/greater-good',
            children: [
              {
                id: 'hop-ggia',
                name: 'ggia',
                kind: 'folder',
                pathLabel: 'hop-studios/clients/greater-good/ggia',
                children: [
                  { id: 'd-ggia-md', name: 'GGIA.md', kind: 'doc', pathLabel: 'hop-studios/clients/greater-good/ggia/GGIA.md' },
                ],
              },
              {
                id: 'hop-ggsc',
                name: 'ggsc',
                kind: 'folder',
                pathLabel: 'hop-studios/clients/greater-good/ggsc',
                children: [
                  {
                    id: 'hop-ggsc-todos',
                    name: 'todos',
                    kind: 'folder',
                    pathLabel: 'hop-studios/clients/greater-good/ggsc/todos',
                    children: [
                      { id: 'd-events-spec', name: '9669131017-events-spec.md', kind: 'doc', pathLabel: 'hop-studios/clients/greater-good/ggsc/todos/9669131017-restructure-events-page-three-sections/README.md' },
                    ],
                  },
                ],
              },
            ],
          },
          {
            id: 'hop-exida',
            name: 'exida',
            kind: 'folder',
            pathLabel: 'hop-studios/clients/exida',
            children: [{ id: 'd-exida-md', name: 'exida.md', kind: 'doc', pathLabel: 'hop-studios/clients/exida/exida.md' }],
          },
        ],
      },
      {
        id: 'hop-internal',
        name: 'internal',
        kind: 'folder',
        pathLabel: 'hop-studios/internal',
        children: [{ id: 'd-scrum', name: 'scrum.md', kind: 'doc', pathLabel: 'hop-studios/users/rowan/scrum.md' }],
      },
    ],
  },
  ggsc: {
    id: 'ggsc-root',
    name: 'ggsc',
    kind: 'folder',
    pathLabel: 'hop-studios/clients/greater-good/ggsc',
    children: [
      {
        id: 'g-todos',
        name: 'todos',
        kind: 'folder',
        pathLabel: 'hop-studios/clients/greater-good/ggsc/todos',
        children: [
          { id: 'd-g-ev', name: '9669131017-restructure-events', kind: 'doc', pathLabel: 'hop-studios/clients/greater-good/ggsc/todos/9669131017-restructure-events-page-three-sections/README.md' },
          { id: 'd-g-es', name: '9796110261-spanish-workbook.md', kind: 'doc', pathLabel: 'hop-studios/clients/greater-good/ggsc/todos/9796110261-add-spanish-language-content-family-well-being-workbook.md' },
        ],
      },
      {
        id: 'g-bd',
        name: 'bridging-differences',
        kind: 'folder',
        pathLabel: 'hop-studios/clients/greater-good/ggsc/bridging-differences',
        children: [],
      },
    ],
  },
  personal: {
    id: 'per-root',
    name: 'personal',
    kind: 'folder',
    pathLabel: 'personal',
    children: [
      {
        id: 'p-house',
        name: 'house-sale',
        kind: 'folder',
        pathLabel: 'personal/house-sale',
        children: [{ id: 'd-driveway', name: '2026-04-22-driveway-quotes.md', kind: 'doc', pathLabel: 'personal/house-sale/2026-04-22-driveway-t-r-vs-matt-boese-quote-comparison.md' }],
      },
      {
        id: 'p-acct',
        name: 'accounting',
        kind: 'folder',
        pathLabel: 'personal/accounting',
        children: [{ id: 'd-tax', name: '2025-personal-tax.md', kind: 'doc', pathLabel: 'personal/accounting/2025-personal-tax.md' }],
      },
      {
        id: 'p-vball',
        name: 'volleyball',
        kind: 'folder',
        pathLabel: 'personal/volleyball',
        children: [{ id: 'd-prva', name: '2026-prva-beach-plan.md', kind: 'doc', pathLabel: 'personal/volleyball/2026-prva-beach-volleyball-business-plan.md' }],
      },
    ],
  },
}

interface SearchDocRow {
  id: string
  title: string
  pathLabel: string
  hubId: string
  snippet: string
}

const MOCK_SEARCH_DOCS: SearchDocRow[] = [
  { id: 'sd1', title: 'GGIA.md', pathLabel: 'hop-studios/clients/greater-good/ggia/GGIA.md', hubId: 'hop', snippet: 'Client overview and open threads for GGIA program.' },
  { id: 'sd2', title: 'exida.md', pathLabel: 'hop-studios/clients/exida/exida.md', hubId: 'hop', snippet: 'Silstat landing page and EE support notes.' },
  { id: 'sd3', title: 'Events page restructure', pathLabel: 'hop-studios/clients/greater-good/ggsc/todos/9669131017-...', hubId: 'ggsc', snippet: 'Three-section events IA - technical spec draft.' },
  { id: 'sd4', title: 'Bridging differences hub', pathLabel: 'hop-studios/clients/greater-good/todos/8033432827-...', hubId: 'hop', snippet: 'Resource hub estimate and Hop specification.' },
  { id: 'sd5', title: '2025 personal tax', pathLabel: 'personal/accounting/2025-personal-tax.md', hubId: 'personal', snippet: 'Deadline checklist and receipts summary.' },
]

function folderRootForHub(hubId: string): FolderEntry {
  return MOCK_FOLDER_ROOT_BY_HUB[hubId] ?? MOCK_FOLDER_ROOT_BY_HUB.hop
}

const KIND_CONFIG: Record<ThreadKind, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  flow:     { label: 'Flow',         color: '#B5722A', bg: '#FEF3E2', icon: Zap },
  captured: { label: 'Captured',     color: '#3B82F6', bg: '#EFF6FF', icon: Link2 },
  prompt:   { label: 'Needs you',    color: '#9333EA', bg: '#F3E8FF', icon: AlertCircle },
  digest:   { label: 'Digest',       color: '#059669', bg: '#ECFDF5', icon: AlignJustify },
  reply:    { label: 'Conversation', color: '#6B7280', bg: '#F3F4F6', icon: MessageSquare },
}

// ============================================================================
// Sub-components
// ============================================================================

function InboxScreen({
  filter, setFilter, threads, hubs, onSelectThread,
}: {
  filter: InboxFilter
  setFilter: (f: InboxFilter) => void
  threads: Thread[]
  hubs: Hub[]
  onSelectThread: (t: Thread) => void
}) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '16px 16px 10px', flexShrink: 0 }}>
        <h1 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 26, fontWeight: 600, color: '#1A1714', marginBottom: 12 }}>
          Inbox
        </h1>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }} className="scroll-hide">
          {(['all', 'flows', 'captured', 'prompts'] as InboxFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                background: filter === f ? '#1A1714' : '#EDE9E4',
                color: filter === f ? '#FAFAF8' : '#6B5E55',
                transition: 'all 0.15s ease', flexShrink: 0,
              }}
            >
              {f === 'all' ? 'All' : f === 'flows' ? 'Flows' : f === 'captured' ? 'Captured' : 'Needs you'}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }} className="scroll-hide">
        {threads.map(thread => {
          const kc = KIND_CONFIG[thread.kind]
          const hub = hubs.find(h => h.id === thread.hubId)
          const KindIcon = kc.icon
          return (
            <div
              key={thread.id}
              onClick={() => onSelectThread(thread)}
              style={{
                padding: '14px 16px', borderBottom: '1px solid #EDE9E4',
                background: thread.unread ? '#FEFDFB' : '#FAFAF8',
                cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'flex-start',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 10, background: kc.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginTop: 1,
              }}>
                <KindIcon size={16} style={{ color: kc.color }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 3 }}>
                  <span style={{
                    fontSize: 14, fontWeight: thread.unread ? 600 : 500, color: '#1A1714',
                    lineHeight: 1.3, flex: 1, marginRight: 8,
                  }}>
                    {thread.title}
                  </span>
                  <span style={{ fontSize: 11, color: '#9C9189', flexShrink: 0, marginTop: 1 }}>{thread.time}</span>
                </div>
                <p className="line-clamp-2" style={{ fontSize: 13, color: '#847A72', lineHeight: 1.45, marginBottom: 7 }}>
                  {thread.preview}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 500, color: kc.color,
                    background: kc.bg, borderRadius: 4, padding: '2px 6px',
                  }}>
                    {kc.label}
                  </span>
                  {hub && (
                    <span style={{
                      fontSize: 11, color: '#9C9189', background: '#F3F0EC',
                      borderRadius: 4, padding: '2px 6px',
                    }}>
                      {hub.name}
                    </span>
                  )}
                  {thread.unread && (
                    <div style={{ width: 6, height: 6, borderRadius: 3, background: '#B5722A', marginLeft: 2 }} />
                  )}
                </div>
              </div>
            </div>
          )
        })}
        <div style={{ height: 16 }} />
      </div>
    </div>
  )
}

function ThreadDetailScreen({
  thread, hubs, onBack, onOpenChat,
}: {
  thread: Thread
  hubs: Hub[]
  onBack: () => void
  onOpenChat: () => void
}) {
  const [replyText, setReplyText] = useState('')
  const kc = KIND_CONFIG[thread.kind]
  const KindIcon = kc.icon
  const hub = hubs.find(h => h.id === thread.hubId)

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 4,
        borderBottom: '1px solid #EDE9E4', flexShrink: 0,
      }}>
        <button
          onClick={onBack}
          style={{
            display: 'flex', alignItems: 'center', gap: 2, padding: '6px 4px',
            border: 'none', background: 'transparent', cursor: 'pointer', color: '#B5722A',
          }}
        >
          <ChevronLeft size={20} />
          <span style={{ fontSize: 16, fontWeight: 500 }}>Inbox</span>
        </button>
        <div style={{ flex: 1 }} />
        <button style={{ padding: '6px 8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#9C9189' }}>
          <Archive size={18} />
        </button>
        <button style={{ padding: '6px 8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#9C9189' }}>
          <MoreHorizontal size={18} />
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }} className="scroll-hide">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{
            width: 26, height: 26, borderRadius: 7, background: kc.bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <KindIcon size={13} style={{ color: kc.color }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 500, color: kc.color }}>{kc.label}</span>
          <span style={{ fontSize: 12, color: '#C5BBAF' }}> · </span>
          <span style={{ fontSize: 12, color: '#9C9189' }}>{hub?.name}</span>
          <span style={{ fontSize: 12, color: '#C5BBAF' }}> · </span>
          <span style={{ fontSize: 12, color: '#9C9189' }}>{thread.time}</span>
        </div>

        <h2 style={{
          fontFamily: "'Lora', Georgia, serif",
          fontSize: 21, fontWeight: 600, color: '#1A1714', lineHeight: 1.3, marginBottom: 14,
        }}>
          {thread.title}
        </h2>

        <p style={{ fontSize: 15, color: '#4A403A', lineHeight: 1.6, marginBottom: 16 }}>
          {thread.body ?? thread.preview}
        </p>

        {thread.kind === 'prompt' && (
          <div style={{ background: '#F5F0EB', borderRadius: 12, padding: '14px', marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1714', marginBottom: 10 }}>
              Changes awaiting approval
            </div>
            {[
              'Hero headline: "Build the future of work" - "Your work, your context"',
              'CTA button: "Get started" - "Start for free"',
              'About section: Remove third paragraph (too technical)',
            ].map((item, i, arr) => (
              <div key={i} style={{
                fontSize: 13, color: '#4A403A', padding: '8px 0',
                borderBottom: i < arr.length - 1 ? '1px solid #EDE9E4' : 'none',
                display: 'flex', gap: 8,
              }}>
                <span style={{ color: '#9C9189', minWidth: 16 }}>{i + 1}.</span>
                <span style={{ lineHeight: 1.4 }}>{item}</span>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={onOpenChat}
          style={{
            width: '100%', padding: '14px', background: '#1A1714', color: '#FAFAF8',
            borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8,
          }}
        >
          <Sparkles size={15} />
          Open in Agent Chat
          <ArrowUpRight size={13} style={{ opacity: 0.55 }} />
        </button>
        <div style={{ height: 16 }} />
      </div>

      <div style={{
        padding: '10px 12px 12px', borderTop: '1px solid #EDE9E4',
        background: '#FAFAF8', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <div style={{
          flex: 1, background: '#F0ECE7', borderRadius: 22, padding: '10px 14px',
          display: 'flex', alignItems: 'center',
        }}>
          <input
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Quick reply..."
            style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, color: '#1A1714', outline: 'none' }}
          />
        </div>
        <button
          onClick={() => setReplyText('')}
          style={{
            width: 40, height: 40, borderRadius: 20, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: replyText ? '#B5722A' : '#EDE9E4', transition: 'background 0.15s ease',
          }}
        >
          <Send size={16} style={{ color: replyText ? '#fff' : '#9C9189' }} />
        </button>
      </div>
    </div>
  )
}

function ChatScreen({ hub }: { hub: Hub }) {
  const [input, setInput] = useState('')
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid #EDE9E4',
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10, background: hub.color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Sparkles size={14} style={{ color: '#fff' }} />
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1714' }}>FlowNotes Agent</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
            <div className="live-dot" style={{ width: 6, height: 6, borderRadius: 3, background: '#2D9B6E', flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: '#2D9B6E', fontWeight: 500 }}>{hub.name} context</span>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px' }} className="scroll-hide">
        {CHAT_MESSAGES.map(msg => (
          <div key={msg.id} style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            alignItems: 'flex-end', gap: 6,
          }}>
            {msg.role === 'assistant' && (
              <div style={{
                width: 24, height: 24, borderRadius: 7, background: hub.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, marginBottom: 4,
              }}>
                <Sparkles size={11} style={{ color: '#fff' }} />
              </div>
            )}
            <div style={{ maxWidth: '80%' }}>
              <div style={{
                padding: '10px 13px',
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
                background: msg.role === 'user' ? '#1A1714' : '#FFFFFF',
                color: msg.role === 'user' ? '#FAFAF8' : '#1A1714',
                fontSize: 14, lineHeight: 1.5,
                boxShadow: msg.role === 'assistant' ? '0 1px 3px rgba(0,0,0,0.07)' : 'none',
              }}>
                {msg.content}
              </div>
              <div style={{
                fontSize: 10, color: '#B5ACA5', marginTop: 3,
                textAlign: msg.role === 'user' ? 'right' : 'left', padding: '0 2px',
              }}>
                {msg.time}
              </div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div style={{
        padding: '10px 12px 12px', borderTop: '1px solid #EDE9E4',
        background: '#FAFAF8', flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center',
      }}>
        <div style={{
          flex: 1, background: '#F0ECE7', borderRadius: 22, padding: '10px 14px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={`Ask about ${hub.name}...`}
            style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 14, color: '#1A1714', outline: 'none' }}
          />
          <Mic size={16} style={{ color: '#9C9189', flexShrink: 0 }} />
        </div>
        <button
          onClick={() => setInput('')}
          style={{
            width: 40, height: 40, borderRadius: 20, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: input ? '#B5722A' : '#EDE9E4', transition: 'background 0.15s ease',
          }}
        >
          <Send size={16} style={{ color: input ? '#fff' : '#9C9189' }} />
        </button>
      </div>
    </div>
  )
}

function FolderBrowseScreen({
  hub,
  stack,
  setStack,
}: {
  hub: Hub
  stack: FolderEntry[]
  setStack: Dispatch<SetStateAction<FolderEntry[]>>
}) {
  const current = stack[stack.length - 1]

  if (current.kind === 'doc') {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px 12px', borderBottom: '1px solid #EDE9E4', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => setStack(s => s.slice(0, -1))}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 4px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#B5722A' }}
          >
            <ChevronLeft size={20} />
            <span style={{ fontSize: 15, fontWeight: 500 }}>Folders</span>
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }} className="scroll-hide">
          <div style={{ fontSize: 11, color: '#9C9189', marginBottom: 8 }}>{hub.name}</div>
          <h2 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 21, fontWeight: 600, color: '#1A1714', marginBottom: 12 }}>{current.name}</h2>
          <p style={{ fontSize: 12, color: '#847A72', lineHeight: 1.5, marginBottom: 16, wordBreak: 'break-word' }}>{current.pathLabel}</p>
          <p style={{ fontSize: 14, color: '#4A403A', lineHeight: 1.6 }}>
            Prototype: document body would load from FlowNotes (read-only sheet on device). Use Chat to edit or summarize.
          </p>
          <button
            type="button"
            style={{
              marginTop: 20, width: '100%', padding: '12px', background: '#1A1714', color: '#FAFAF8',
              borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <Sparkles size={14} /> Ask agent about this doc
          </button>
        </div>
      </div>
    )
  }

  const items = current.children ?? []

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid #EDE9E4',
        display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
      }}>
        {stack.length > 1 ? (
          <button
            type="button"
            onClick={() => setStack(s => s.slice(0, -1))}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: 10, border: 'none', background: '#F0ECE7', cursor: 'pointer', color: '#1A1714',
            }}
          >
            <ChevronLeft size={20} />
          </button>
        ) : (
          <div style={{ width: 36 }} />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#9C9189' }}>{hub.name}</div>
          <div style={{ fontSize: 17, fontWeight: 600, color: '#1A1714', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {current.name}
          </div>
          <div style={{ fontSize: 11, color: '#B5ACA5', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {current.pathLabel}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }} className="scroll-hide">
        {items.length === 0 ? (
          <div style={{ padding: '28px 16px', textAlign: 'center', color: '#9C9189', fontSize: 14 }}>
            Empty folder
          </div>
        ) : (
          items.map(entry => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setStack(s => [...s, entry])}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 16px', border: 'none', borderBottom: '1px solid #EDE9E4',
                background: '#FAFAF8', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: entry.kind === 'folder' ? '#F0ECE7' : '#FEF3E2',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {entry.kind === 'folder'
                  ? <Folder size={18} style={{ color: '#92400e' }} />
                  : <FileText size={18} style={{ color: '#B5722A' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 500, color: '#1A1714' }}>{entry.name}</div>
                <div style={{ fontSize: 11, color: '#9C9189', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {entry.pathLabel}
                </div>
              </div>
              {entry.kind === 'folder' ? <ChevronRight size={16} style={{ color: '#C5BBAF', flexShrink: 0 }} /> : null}
            </button>
          ))
        )}
      </div>
    </div>
  )
}

function BrowseSearchPanel({
  query,
  setQuery,
  hub,
  onOpenThread,
}: {
  query: string
  setQuery: (q: string) => void
  hub: Hub
  onOpenThread: (t: Thread) => void
}) {
  const q = query.trim().toLowerCase()
  const scopeThreads = THREADS.filter(t => t.hubId === hub.id)
  const threadsMatch = q
    ? THREADS.filter(t =>
        t.title.toLowerCase().includes(q) || t.preview.toLowerCase().includes(q),
      )
    : []
  const docsMatch = q
    ? MOCK_SEARCH_DOCS.filter(d =>
        d.title.toLowerCase().includes(q) ||
        d.pathLabel.toLowerCase().includes(q) ||
        d.snippet.toLowerCase().includes(q),
      )
    : []

  const showScopedHint = !q

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '0 16px 12px', flexShrink: 0 }}>
        <div style={{
          background: '#F0ECE7', borderRadius: 12, padding: '12px 14px',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Search size={16} style={{ color: '#9C9189', flexShrink: 0 }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Documents, paths, inbox titles..."
            aria-label="Search documents and threads"
            style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 15, color: '#1A1714', outline: 'none' }}
          />
          {query ? (
            <button type="button" onClick={() => setQuery('')} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4 }}>
              <X size={16} style={{ color: '#9C9189' }} />
            </button>
          ) : null}
        </div>
        <div style={{ fontSize: 11, color: '#B5ACA5', marginTop: 8, lineHeight: 1.4 }}>
          Active workspace: <strong style={{ color: '#6B5E55' }}>{hub.name}</strong>. {scopeThreads.length} inbox threads in this hub (mock).
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }} className="scroll-hide">
        {showScopedHint && (
          <div style={{ padding: '8px 16px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9C9189', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Try searching
            </div>
            <p style={{ fontSize: 13, color: '#847A72', lineHeight: 1.5, marginBottom: 12 }}>
              Type to filter mock threads and docs. Examples: GGIA, events, tax, digest.
            </p>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9C9189', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              In this workspace (threads)
            </div>
            {scopeThreads.slice(0, 4).map(t => {
              const kc = KIND_CONFIG[t.kind]
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpenThread(t)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '11px 0', borderBottom: '1px solid #EDE9E4',
                    borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                    background: 'transparent', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: kc.color, flexShrink: 0, marginTop: 5 }} />
                  <span style={{ fontSize: 14, color: '#1A1714', flex: 1, lineHeight: 1.35 }}>{t.title}</span>
                  <ChevronRight size={14} style={{ color: '#C5BBAF', flexShrink: 0 }} />
                </button>
              )
            })}
          </div>
        )}

        {!showScopedHint && threadsMatch.length === 0 && docsMatch.length === 0 && (
          <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9C9189', fontSize: 14 }}>
            No results for &quot;{query.trim()}&quot;
          </div>
        )}

        {!showScopedHint && threadsMatch.length > 0 && (
          <div style={{ padding: '0 16px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9C9189', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Inbox threads ({threadsMatch.length})
            </div>
            {threadsMatch.map(t => {
              const kc = KIND_CONFIG[t.kind]
              const th = HUBS.find(h => h.id === t.hubId)
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onOpenThread(t)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10,
                    padding: '11px 0', borderBottom: '1px solid #EDE9E4',
                    borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                    background: 'transparent', cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: kc.color, flexShrink: 0, marginTop: 5 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: '#1A1714', lineHeight: 1.35 }}>{t.title}</div>
                    <div style={{ fontSize: 11, color: '#9C9189', marginTop: 3 }}>{th?.name}</div>
                  </div>
                  <ChevronRight size={14} style={{ color: '#C5BBAF', flexShrink: 0 }} />
                </button>
              )
            })}
          </div>
        )}

        {!showScopedHint && docsMatch.length > 0 && (
          <div style={{ padding: '0 16px 24px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9C9189', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Documents ({docsMatch.length})
            </div>
            {docsMatch.map(d => {
              const dh = HUBS.find(h => h.id === d.hubId)
              return (
                <div
                  key={d.id}
                  style={{
                    padding: '12px 0', borderBottom: '1px solid #EDE9E4',
                    display: 'flex', gap: 12, alignItems: 'flex-start',
                  }}
                >
                  <FileText size={16} style={{ color: '#B5722A', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 500, color: '#1A1714' }}>{d.title}</div>
                    <div style={{ fontSize: 11, color: '#9C9189', marginTop: 3, wordBreak: 'break-word' }}>{d.pathLabel}</div>
                    <div style={{ fontSize: 13, color: '#847A72', marginTop: 6, lineHeight: 1.45 }}>{d.snippet}</div>
                    <div style={{ fontSize: 10, color: '#C5BBAF', marginTop: 4 }}>{dh?.name}</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Main export
// ============================================================================

export function MobileAppTest() {
  const [activeTab, setActiveTab] = useState<Screen>('inbox')
  const [selectedThread, setSelectedThread] = useState<Thread | null>(null)
  const [activeHub, setActiveHub] = useState<Hub>(HUBS[1])
  const [showHubPicker, setShowHubPicker] = useState(false)
  const [showCapture, setShowCapture] = useState(false)
  const [inboxFilter, setInboxFilter] = useState<InboxFilter>('all')
  const [captureText, setCaptureText] = useState('')
  const [hubSwitchAnim, setHubSwitchAnim] = useState(false)
  const [browseSegment, setBrowseSegment] = useState<BrowseSegment>('folders')
  const [folderStack, setFolderStack] = useState<FolderEntry[]>(() => [folderRootForHub(HUBS[1].id)])
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    setFolderStack([folderRootForHub(activeHub.id)])
  }, [activeHub.id])

  const filteredThreads = THREADS.filter(t => {
    if (inboxFilter === 'all') return true
    if (inboxFilter === 'flows') return t.kind === 'flow' || t.kind === 'digest'
    if (inboxFilter === 'captured') return t.kind === 'captured'
    if (inboxFilter === 'prompts') return t.kind === 'prompt'
    return true
  })

  const handleHubSwitch = (hub: Hub) => {
    setActiveHub(hub)
    setShowHubPicker(false)
    setHubSwitchAnim(true)
    setTimeout(() => setHubSwitchAnim(false), 700)
  }

  const handleOpenChat = () => {
    setActiveTab('chat')
    setSelectedThread(null)
  }

  const totalUnread = THREADS.filter(t => t.unread).length

  const screenLabel = (() => {
    if (activeTab === 'inbox' && !selectedThread) return 'Inbox - thread list'
    if (activeTab === 'inbox' && selectedThread) return 'Thread detail - quick reply + Open in Chat'
    if (activeTab === 'chat') return 'Agent chat - hub-primed conversation'
    if (activeTab === 'browse' && browseSegment === 'folders') return 'Browse - folder tree (per workspace)'
    if (activeTab === 'browse' && browseSegment === 'search') return 'Browse - search threads + documents (mock)'
    if (activeTab === 'profile') return 'Profile + workspace list'
    return ''
  })()

  return (
    <div
      style={{
        height: '100vh', width: '100vw', display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(145deg, #E5DFD8 0%, #CEC6BE 100%)',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      {/* Font + animation styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes hubPulse { 0%,100% { opacity:1; } 40% { opacity:0.45; } }
        @keyframes pulseDot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.55; transform:scale(0.8); } }
        .sheet-enter { animation: slideUp 0.3s cubic-bezier(0.32,0.72,0,1) forwards; }
        .overlay-enter { animation: fadeIn 0.18s ease forwards; }
        .hub-anim { animation: hubPulse 0.55s ease forwards; }
        .live-dot { animation: pulseDot 1.6s ease-in-out infinite; }
        .scroll-hide::-webkit-scrollbar { display: none; }
        .scroll-hide { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>

      {/* Title badge */}
      <div style={{ marginBottom: 20, textAlign: 'center' }}>
        <span style={{ fontSize: 13, fontWeight: 500, color: '#A89C93', letterSpacing: '0.02em' }}>
          FlowNotes - Mobile Prototype
        </span>
      </div>

      {/* iPhone 15 frame */}
      <div style={{
        width: 390, height: 844, background: '#FAFAF8', borderRadius: 44,
        boxShadow: '0 48px 96px rgba(0,0,0,0.38), 0 0 0 1px rgba(0,0,0,0.12), inset 0 0 0 1.5px rgba(255,255,255,0.25)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>

        {/* Status bar */}
        <div style={{
          height: 52, background: '#FAFAF8', paddingTop: 14, paddingLeft: 28, paddingRight: 24,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1A1714' }}>11:46</span>
          <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
            <svg width="17" height="12" viewBox="0 0 17 12"><rect x="0" y="7" width="3" height="5" rx="1" fill="#1A1714"/><rect x="4.5" y="5" width="3" height="7" rx="1" fill="#1A1714"/><rect x="9" y="2.5" width="3" height="9.5" rx="1" fill="#1A1714"/><rect x="13.5" y="0" width="3" height="12" rx="1" fill="#1A1714"/></svg>
            <svg width="16" height="12" viewBox="0 0 16 12"><path d="M8 10.5a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4z" fill="#1A1714"/><path d="M4.5 7.5C5.4 6.6 6.6 6 8 6s2.6.6 3.5 1.5" stroke="#1A1714" strokeWidth="1.4" strokeLinecap="round"/><path d="M2 5C3.4 3.5 5.6 2.5 8 2.5s4.6 1 6 2.5" stroke="#1A1714" strokeWidth="1.4" strokeLinecap="round"/></svg>
            <svg width="26" height="12" viewBox="0 0 26 12"><rect x="0.5" y="0.5" width="21" height="11" rx="3.5" stroke="#1A1714" strokeOpacity="0.35"/><rect x="2" y="2" width="16" height="8" rx="2" fill="#1A1714"/><path d="M22.5 4.5v3a1.5 1.5 0 000-3z" fill="#1A1714" fillOpacity="0.4"/></svg>
          </div>
        </div>

        {/* Hub strip */}
        <div
          className={hubSwitchAnim ? 'hub-anim' : ''}
          style={{
            height: 48, background: '#FAFAF8', borderBottom: '1px solid #EDE9E4',
            paddingLeft: 14, paddingRight: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
          }}
        >
          <button
            onClick={() => setShowHubPicker(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
              borderRadius: 22, background: activeHub.color + '18', border: 'none', cursor: 'pointer',
            }}
          >
            <div style={{
              width: 20, height: 20, borderRadius: 6, background: activeHub.color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>
                {activeHub.shortName.charAt(0)}
              </span>
            </div>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1714' }}>{activeHub.name}</span>
            <ChevronRight size={13} style={{ color: '#9C9189', marginLeft: -3 }} />
          </button>
          {activeHub.pendingCount > 0 && (
            <span style={{
              fontSize: 12, fontWeight: 600, color: '#B5722A',
              background: '#FEF3E2', borderRadius: 10, padding: '3px 9px',
            }}>
              {activeHub.pendingCount} pending
            </span>
          )}
        </div>

        {/* Screen area */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>

          {/* Inbox */}
          {activeTab === 'inbox' && !selectedThread && (
            <InboxScreen
              filter={inboxFilter}
              setFilter={setInboxFilter}
              threads={filteredThreads}
              hubs={HUBS}
              onSelectThread={setSelectedThread}
            />
          )}

          {/* Thread detail */}
          {activeTab === 'inbox' && selectedThread && (
            <ThreadDetailScreen
              thread={selectedThread}
              hubs={HUBS}
              onBack={() => setSelectedThread(null)}
              onOpenChat={handleOpenChat}
            />
          )}

          {/* Chat */}
          {activeTab === 'chat' && <ChatScreen hub={activeHub} />}

          {/* Browse: folders + search */}
          {activeTab === 'browse' && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px 10px', flexShrink: 0 }}>
                <h1 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 26, fontWeight: 600, color: '#1A1714', marginBottom: 12 }}>
                  Browse
                </h1>
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }} className="scroll-hide">
                  {(['folders', 'search'] as BrowseSegment[]).map(seg => (
                    <button
                      key={seg}
                      type="button"
                      onClick={() => {
                        setBrowseSegment(seg)
                        if (seg === 'folders') setFolderStack([folderRootForHub(activeHub.id)])
                      }}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 500,
                        border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                        background: browseSegment === seg ? '#1A1714' : '#EDE9E4',
                        color: browseSegment === seg ? '#FAFAF8' : '#6B5E55',
                        transition: 'all 0.15s ease', flexShrink: 0,
                      }}
                    >
                      {seg === 'folders' ? 'Folders' : 'Search'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
                {browseSegment === 'folders' ? (
                  <FolderBrowseScreen hub={activeHub} stack={folderStack} setStack={setFolderStack} />
                ) : (
                  <BrowseSearchPanel
                    query={searchQuery}
                    setQuery={setSearchQuery}
                    hub={activeHub}
                    onOpenThread={(t) => {
                      setSelectedThread(t)
                      setActiveTab('inbox')
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Profile */}
          {activeTab === 'profile' && (
            <div style={{ height: '100%', padding: '16px', overflowY: 'auto' }} className="scroll-hide">
              <h1 style={{ fontFamily: "'Lora', Georgia, serif", fontSize: 26, fontWeight: 600, color: '#1A1714', marginBottom: 20 }}>Profile</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
                <div style={{
                  width: 58, height: 58, borderRadius: 18, background: '#B5722A',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, fontWeight: 700, color: '#fff',
                }}>
                  R
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#1A1714' }}>Rowan</div>
                  <div style={{ fontSize: 13, color: '#9C9189', marginTop: 2 }}>rowan@hopstudios.com</div>
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: '#9C9189', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                Workspaces
              </div>
              {HUBS.map(hub => (
                <div
                  key={hub.id}
                  onClick={() => handleHubSwitch(hub)}
                  style={{
                    padding: '13px 0', borderBottom: '1px solid #EDE9E4',
                    display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 12, background: hub.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 15, fontWeight: 700, color: '#fff', flexShrink: 0,
                  }}>
                    {hub.shortName.charAt(0)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: '#1A1714' }}>{hub.name}</div>
                    <div style={{ fontSize: 12, color: '#9C9189', marginTop: 1 }}>{hub.lastActivity}</div>
                  </div>
                  {hub.id === activeHub.id
                    ? <CheckCircle2 size={18} style={{ color: '#B5722A' }} />
                    : hub.pendingCount > 0
                      ? <span style={{ fontSize: 12, fontWeight: 600, color: '#B5722A', background: '#FEF3E2', borderRadius: 10, padding: '2px 8px' }}>{hub.pendingCount}</span>
                      : null
                  }
                </div>
              ))}
            </div>
          )}

          {/* Hub picker sheet */}
          {showHubPicker && (
            <>
              <div
                className="overlay-enter"
                onClick={() => setShowHubPicker(false)}
                style={{ position: 'absolute', inset: 0, background: 'rgba(26,23,20,0.48)', zIndex: 10 }}
              />
              <div className="sheet-enter" style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: '#FAFAF8', borderRadius: '22px 22px 0 0',
                paddingBottom: 20, zIndex: 11,
                boxShadow: '0 -10px 48px rgba(0,0,0,0.14)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 2px' }}>
                  <div style={{ width: 36, height: 4, borderRadius: 2, background: '#C5BBAF' }} />
                </div>
                <div style={{ padding: '10px 20px 14px', fontSize: 12, fontWeight: 600, color: '#9C9189', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  Switch workspace
                </div>
                {HUBS.map(hub => (
                  <button
                    key={hub.id}
                    onClick={() => handleHubSwitch(hub)}
                    style={{
                      width: '100%', padding: '13px 20px', border: 'none', cursor: 'pointer',
                      background: hub.id === activeHub.id ? hub.color + '10' : 'transparent',
                      display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
                    }}
                  >
                    <div style={{
                      width: 46, height: 46, borderRadius: 14, background: hub.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0,
                    }}>
                      {hub.shortName.charAt(0)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 16, fontWeight: hub.id === activeHub.id ? 600 : 500, color: '#1A1714' }}>
                        {hub.name}
                      </div>
                      <div style={{ fontSize: 12, color: '#9C9189', marginTop: 2 }}>
                        {hub.pendingCount > 0 ? `${hub.pendingCount} pending` : 'Up to date'} - {hub.lastActivity}
                      </div>
                    </div>
                    {hub.id === activeHub.id && (
                      <CheckCircle2 size={20} style={{ color: hub.color }} />
                    )}
                  </button>
                ))}
                <div style={{ padding: '10px 20px 0' }}>
                  <button
                    onClick={() => setShowHubPicker(false)}
                    style={{
                      width: '100%', padding: '14px', background: '#EDE9E4',
                      borderRadius: 12, border: 'none', cursor: 'pointer',
                      fontSize: 15, fontWeight: 500, color: '#4A403A',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Capture sheet */}
          {showCapture && (
            <>
              <div
                className="overlay-enter"
                onClick={() => setShowCapture(false)}
                style={{ position: 'absolute', inset: 0, background: 'rgba(26,23,20,0.48)', zIndex: 10 }}
              />
              <div className="sheet-enter" style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: '#FAFAF8', borderRadius: '22px 22px 0 0',
                paddingBottom: 20, zIndex: 11,
                boxShadow: '0 -10px 48px rgba(0,0,0,0.14)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 2px' }}>
                  <div style={{ width: 36, height: 4, borderRadius: 2, background: '#C5BBAF' }} />
                </div>
                <div style={{ padding: '8px 18px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 18, fontWeight: 600, color: '#1A1714' }}>Quick capture</span>
                  <button onClick={() => setShowCapture(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4 }}>
                    <X size={20} style={{ color: '#9C9189' }} />
                  </button>
                </div>

                <div style={{ display: 'flex', gap: 8, padding: '0 18px 14px', overflowX: 'auto' }} className="scroll-hide">
                  {([{ id: 'note', icon: FileText, label: 'Note' }, { id: 'link', icon: Link2, label: 'Link' }, { id: 'voice', icon: Mic, label: 'Voice' }] as const).map(({ id, icon: Icon, label }, i) => (
                    <button
                      key={id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                        borderRadius: 22, border: '1.5px solid',
                        borderColor: i === 0 ? '#B5722A' : '#EDE9E4',
                        background: i === 0 ? '#FEF3E2' : 'transparent',
                        color: i === 0 ? '#B5722A' : '#6B5E55',
                        fontSize: 14, fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      <Icon size={14} />{label}
                    </button>
                  ))}
                </div>

                <div style={{ padding: '0 18px 12px' }}>
                  <textarea
                    value={captureText}
                    onChange={e => setCaptureText(e.target.value)}
                    placeholder="What's on your mind..."
                    rows={4}
                    style={{
                      width: '100%', background: '#F0ECE7', border: 'none', borderRadius: 12,
                      padding: '12px 14px', fontSize: 15, color: '#1A1714', outline: 'none',
                      resize: 'none', lineHeight: 1.5, boxSizing: 'border-box',
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                    }}
                  />
                </div>

                <div style={{ padding: '0 18px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, color: '#9C9189' }}>Route to</span>
                  <button style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                    borderRadius: 18, background: activeHub.color + '16', border: 'none', cursor: 'pointer',
                  }}>
                    <div style={{ width: 12, height: 12, borderRadius: 4, background: activeHub.color }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: '#1A1714' }}>{activeHub.name}</span>
                    <ChevronRight size={11} style={{ color: '#9C9189' }} />
                  </button>
                </div>

                <div style={{ padding: '0 18px' }}>
                  <button
                    onClick={() => { setCaptureText(''); setShowCapture(false) }}
                    style={{
                      width: '100%', padding: '14px',
                      background: captureText ? '#1A1714' : '#EDE9E4',
                      color: captureText ? '#FAFAF8' : '#9C9189',
                      borderRadius: 12, border: 'none', cursor: 'pointer',
                      fontSize: 15, fontWeight: 600, transition: 'all 0.15s ease',
                      fontFamily: "'DM Sans', system-ui, sans-serif",
                    }}
                  >
                    Capture to inbox
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom tab bar */}
        <div style={{
          height: 82, background: '#FAFAF8', borderTop: '1px solid #EDE9E4',
          display: 'flex', alignItems: 'flex-start', paddingTop: 8,
          paddingLeft: 4, paddingRight: 4, flexShrink: 0,
        }}>
          {([
            { id: 'inbox',   icon: Inbox,          label: 'Inbox',  isAction: false },
            { id: 'chat',    icon: MessageSquare,  label: 'Chat',   isAction: false },
            { id: 'capture', icon: Plus,           label: '',       isAction: true  },
            { id: 'browse',  icon: Folder,         label: 'Browse', isAction: false },
            { id: 'profile', icon: User,           label: 'Me',     isAction: false },
          ] as const).map(({ id, icon: Icon, label, isAction }) => {
            const isActive = activeTab === (id as Screen)

            if (isAction) {
              return (
                <div key="capture" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowCapture(true)}
                    style={{
                      width: 50, height: 50, borderRadius: 16, background: '#B5722A',
                      border: 'none', cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', marginTop: -5,
                      boxShadow: '0 5px 18px rgba(181,114,42,0.42)',
                    }}
                  >
                    <Icon size={22} style={{ color: '#fff', strokeWidth: 2.5 }} />
                  </button>
                </div>
              )
            }

            return (
              <button
                key={id}
                onClick={() => {
                  setActiveTab(id as Screen)
                  if (id === 'inbox') setSelectedThread(null)
                  if (id === 'browse') {
                    setBrowseSegment('folders')
                    setFolderStack([folderRootForHub(activeHub.id)])
                  }
                }}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 3, padding: '2px 0', border: 'none', background: 'transparent',
                  cursor: 'pointer', position: 'relative',
                }}
              >
                <div style={{ position: 'relative' }}>
                  <Icon size={22} style={{
                    color: isActive ? '#B5722A' : '#9C9189',
                    strokeWidth: isActive ? 2.2 : 1.8,
                    transition: 'color 0.15s ease',
                  }} />
                  {id === 'inbox' && totalUnread > 0 && (
                    <div style={{
                      position: 'absolute', top: -3, right: -5,
                      width: 16, height: 16, borderRadius: 8,
                      background: '#B5722A', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 700, color: '#fff',
                    }}>
                      {totalUnread}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize: 10, fontWeight: 500,
                  color: isActive ? '#B5722A' : '#9C9189',
                  transition: 'color 0.15s ease',
                }}>
                  {label}
                </span>
              </button>
            )
          })}
        </div>

        {/* Home indicator */}
        <div style={{
          height: 10, background: '#FAFAF8', flexShrink: 0,
          display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 4,
        }}>
          <div style={{ width: 120, height: 4, borderRadius: 2, background: '#1A1714', opacity: 0.16 }} />
        </div>
      </div>

      {/* Context label below phone */}
      <div style={{ marginTop: 18, textAlign: 'center', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
        <p style={{ fontSize: 13, color: '#9C9189', fontWeight: 500 }}>{screenLabel}</p>
        <p style={{ fontSize: 11, color: '#B5ACA5', marginTop: 3 }}>
          Browse tab: Folders (tree) and Search (mock). Switch workspace from the hub strip.
        </p>
      </div>
    </div>
  )
}
